import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Bell, AlertTriangle, AlertCircle, Clock, CheckCircle2,
  CreditCard, Calendar, FileText, X, ChevronRight, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ICONS = {
  'alert-triangle': AlertTriangle, 'alert-circle': AlertCircle, 'clock': Clock,
  'check-circle': CheckCircle2, 'credit-card': CreditCard, 'calendar': Calendar, 'file-text': FileText
};

const PRIO = {
  critical: { text: 'text-red-700', icon: 'text-red-500', border: 'border-l-red-500' },
  warning: { text: 'text-amber-700', icon: 'text-amber-500', border: 'border-l-amber-500' },
  info: { text: 'text-gray-900', icon: 'text-blue-500', border: 'border-l-blue-500' }
};

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('tenant_token') || localStorage.getItem('token') || ''}`,
  'X-Tenant-DB': localStorage.getItem('tenant_db') || ''
});

export const formatQuando = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const ora = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  const oggi = new Date(); const ieri = new Date(); ieri.setDate(oggi.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, oggi)) return `Oggi ${ora}`;
  if (same(d, ieri)) return `Ieri ${ora}`;
  return `${d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })} ${ora}`;
};

const NotificaItem = ({ n, letta, onClick, mostraOra }) => {
  const p = PRIO[n.priorita] || PRIO.info;
  const Icon = ICONS[n.icona] || Bell;
  return (
    <div
      data-testid={`notifica-item-${n.tipo}`}
      onClick={() => onClick(n)}
      className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-l-4 ${p.border} ${letta ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${p.icon}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className={`font-medium text-sm truncate ${p.text}`}>{n.titolo}</p>
            {mostraOra && (
              <span data-testid="notifica-quando" className="text-[11px] text-gray-400 whitespace-nowrap flex-shrink-0">
                {formatQuando(n.data)}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{n.messaggio}</p>
        </div>
        {!letta ? <span className="w-2 h-2 mt-1.5 bg-blue-500 rounded-full flex-shrink-0" />
                : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />}
      </div>
    </div>
  );
};

const NotificationBell = ({ onNavigate, darkMode = true }) => {
  const [avvisi, setAvvisi] = useState([]);
  const [situazione, setSituazione] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [lette, setLette] = useState(new Set());
  const dropdownRef = useRef(null);

  const loadNotifiche = useCallback(async () => {
    if (!localStorage.getItem('tenant_db')) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/notifiche`, { headers: authHeaders() });
      if (res.data.success) {
        setAvvisi(res.data.avvisi || []);
        setSituazione(res.data.situazione || []);
        setLette(new Set((res.data.notifiche || []).filter(n => n.letta).map(n => n.id)));
      }
    } catch (error) {
      console.error('Errore caricamento notifiche:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifiche();
    const interval = setInterval(loadNotifiche, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadNotifiche]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const segnaLette = (ids, tutte = false) => {
    setLette(prev => new Set([...prev, ...ids]));
    axios.post(`${API}/api/notifiche/tutte-lette`, { ids, tutte }, { headers: authHeaders() })
      .then(() => { if (tutte) loadNotifiche(); })
      .catch(() => {});
  };

  const handleNotificaClick = (n) => {
    segnaLette([n.id]);
    setIsOpen(false);
    if (onNavigate && n.azione) onNavigate(n.azione, n.sezione ? { sezione: n.sezione } : null);
  };

  const tutte = [...avvisi, ...situazione];
  const nonLette = tutte.filter(n => !lette.has(n.id)).length;
  const criticalNonLette = tutte.some(n => n.priorita === 'critical' && !lette.has(n.id));

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => { setIsOpen(o => !o); if (!isOpen) loadNotifiche(); }}
        className={`relative p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
        data-testid="notification-bell"
        aria-label="Notifiche"
      >
        <Bell className={`h-6 w-6 ${darkMode ? 'text-white' : 'text-gray-700'}`} />
        {nonLette > 0 && (
          <span data-testid="notification-badge" className={`absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center px-1 text-xs font-bold rounded-full text-white ${
            criticalNonLette ? 'bg-red-500 animate-pulse' : 'bg-amber-500'}`}>
            {nonLette > 99 ? '99+' : nonLette}
          </span>
        )}
      </button>

      {isOpen && (
        <div data-testid="notification-dropdown" className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50">
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-white" />
              <span className="font-semibold text-white">Notifiche</span>
              {nonLette > 0 && (
                <span data-testid="notification-nuove" className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">{nonLette} nuove</span>
              )}
            </div>
            <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/10 rounded-full transition-colors" aria-label="Chiudi">
              <X className="h-4 w-4 text-white" />
            </button>
          </div>

          <div className="max-h-[70vh] sm:max-h-96 overflow-y-auto">
            {loading && tutte.length === 0 ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 text-gray-400 animate-spin" /></div>
            ) : tutte.length === 0 ? (
              <div className="text-center py-8 px-4" data-testid="notifiche-vuoto">
                <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Nessuna notifica</p>
                <p className="text-gray-400 text-sm">Tutto in ordine!</p>
              </div>
            ) : (
              <>
                <div data-testid="notifiche-avvisi">
                  <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Avvisi recenti</p>
                  {avvisi.length === 0 ? (
                    <p className="px-4 pb-3 text-sm text-gray-400" data-testid="avvisi-vuoto">Nessun avviso negli ultimi 7 giorni</p>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {avvisi.map(n => <NotificaItem key={n.id} n={n} letta={lette.has(n.id)} onClick={handleNotificaClick} mostraOra />)}
                    </div>
                  )}
                </div>
                {situazione.length > 0 && (
                  <div data-testid="notifiche-situazione" className="border-t border-gray-200">
                    <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Situazione</p>
                    <div className="divide-y divide-gray-100">
                      {situazione.map(n => <NotificaItem key={n.id} n={n} letta={lette.has(n.id)} onClick={handleNotificaClick} mostraOra={false} />)}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {tutte.length > 0 && (
            <div className="border-t border-gray-200 px-4 py-2 bg-gray-50 flex items-center justify-between">
              <span className="text-xs text-gray-400">{avvisi.length} avvisi · {situazione.length} situazione</span>
              <button
                data-testid="segna-tutte-lette-btn"
                onClick={() => segnaLette(tutte.map(n => n.id), true)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Segna tutte come lette
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
